import React, { useRef } from 'react';
import "react-data-grid/lib/styles.css";
import { DataGrid } from 'react-data-grid';

const InlineTextEditor = ({ row, column, onRowChange, onClose }) => {
  const value = row[column.key] ?? "";

  const commitAndClose = () => {
    if (typeof onClose === 'function') onClose(true);
  };

  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => onRowChange({ ...row, [column.key]: e.target.value }, true)}
      onBlur={commitAndClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') commitAndClose();
        if (e.key === 'Escape' && typeof onClose === 'function') onClose();
      }}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '0 6px',
        fontSize: 12,
        boxSizing: 'border-box'
      }}
    />
  );
};
/**
 * G06BlockDataGrid - fully controlled grid for the G06 page.
 *
 * The parent owns row data via the `data` prop and receives every change
 * through `onDataChange`. Do NOT add an internal useState mirror: a mirror
 * synced via useEffect tears down the active editor cell mid-keystroke
 * and breaks typing right after a paste.
 *
 * Each editable column must declare renderEditCell. react-data-grid v7
 * will not open an editor on a printable key press without it; `editable:
 * true` alone only enables cell selection.
 *
 * Paste is Excel-style: the pasted block overlays cells starting at the
 * currently-selected cell and runs as far down/right as the clipboard
 * extends. Cells outside that rectangle are untouched.
 *
 * @param {function} onDataChange - Callback when data changes (cell edit or paste)
 * @param {object[]} columnDefs - Column definitions: { key, name, headerRenderer, width, minWidth, editable }
 * @param {object[]} data - Controlled row data from parent
 * @param {number} rowCount - Number of rows to display
 */
export const G06BlockDataGrid = ({
  onDataChange,
  columnDefs = [],
  data = null,
  rowCount = 10
}) => {
  const getColumnKeys = () => columnDefs.map(d => d.key);
  const getEditableColumnKeys = () => columnDefs.filter(d => d.editable !== false).map(d => d.key);

  const columns = columnDefs.map(def => {
    const editable = def.editable !== false;
    return {
      key: def.key,
      name: def.name,
      editable,
      renderHeaderCell: def.headerRenderer || undefined,
      width: def.width || undefined,
      minWidth: def.minWidth || undefined,
      renderEditCell: editable ? InlineTextEditor : undefined
    };
  });

  // Fully controlled: rows come straight from the parent's `data` prop.
  const rows = Array.isArray(data) ? data : [];

  // Track the most recently selected cell so paste can be position-aware
  // (Excel-style: pasted block lands at the selection, not at A1).
  // react-data-grid v7 fires onSelectedCellChange with { rowIdx, row, column }.
  // Some builds also expose `idx` directly on the args object - read both.
  const selectedCellRef = useRef({ rowIdx: 0, idx: 0 });
  const handleSelectedCellChange = (args) => {
    const rowIdx = args?.rowIdx ?? 0;
    const idx = args?.idx ?? args?.column?.idx ?? 0;
    selectedCellRef.current = { rowIdx, idx };
  };

  const handleRowsChange = (newRows) => {
    if (onDataChange) onDataChange(newRows);
  };

  // Excel-style paste: starts at the currently-selected cell and overlays
  // values across the destination range.  Cells outside that range stay
  // untouched.  Non-editable target columns (e.g. the RCC `Ref` column)
  // are skipped silently while still consuming the corresponding pasted
  // column - so the visual paste alignment matches the user's clipboard
  // layout column-for-column.
  const handlePaste = (event) => {
    event.preventDefault();
    const clipboardText = event.clipboardData.getData("text");
    const pastedRows = clipboardText
      // Normalize line endings and split into lines
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      // Split each line by tab into columns
      .map((row) => row.split("\t"));

    // Drop any trailing empty line that came from a clipboard with a final
    // newline, but DO NOT trim() the whole text - that would also strip
    // leading/trailing whitespace inside individual cells.
    while (pastedRows.length > 0 && pastedRows[pastedRows.length - 1].every(c => c === "")) {
      pastedRows.pop();
    }
    if (pastedRows.length === 0) return;

    const keys = getColumnKeys();
    const editableKeys = new Set(getEditableColumnKeys());
    const startRow = Math.max(0, selectedCellRef.current.rowIdx | 0);
    const startCol = Math.max(0, selectedCellRef.current.idx | 0);

    // Start from a structural copy of the current rows so anything outside
    // the paste rectangle is preserved verbatim (including non-editable
    // columns and rows above/below the selection).  Pad to rowCount if
    // the parent hasn't supplied a full block yet.
    const newData = rows.map((r) => ({ ...r }));
    while (newData.length < rowCount) {
      const i = newData.length;
      const blank = { id: i + 1 };
      for (const k of keys) blank[k] = "";
      newData.push(blank);
    }

    for (let pr = 0; pr < pastedRows.length; pr++) {
      const targetRow = startRow + pr;
      if (targetRow >= rowCount) break;
      const pastedRow = pastedRows[pr];
      for (let pc = 0; pc < pastedRow.length; pc++) {
        const targetCol = startCol + pc;
        if (targetCol >= keys.length) break;
        const key = keys[targetCol];
        if (!editableKeys.has(key)) continue;
        newData[targetRow][key] = pastedRow[pc];
      }
    }

    if (onDataChange) onDataChange(newData);
  };

  const gridHeight = rowCount * 35 + 55;

  return (
    <div style={{ width: '100%' }} onPaste={handlePaste}>
      <DataGrid
        rows={rows}
        columns={columns}
        onRowsChange={handleRowsChange}
        onSelectedCellChange={handleSelectedCellChange}
        style={{ height: gridHeight, overflowY: 'hidden' }}
      />
    </div>
  );
};
